// src/components/shell/BottomNav.tsx

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { type LucideIcon } from 'lucide-react';

export type AppId = 'drillholes' | 'surface-mapping' | 'lab-testing' | 'geotechnical-model' | 'ground-support';

export interface NavItem {
  id: AppId;
  label: string;
  icon: LucideIcon;
}

interface BottomNavProps {
  items: NavItem[];
  activeApp: AppId;
  onSelect: (app: AppId) => void;
}

// const DRAWER_HEIGHT = 72 + 16; // item height * items + padding — recalculated below dynamically
const SNAP_THRESHOLD = 40;     // px of drag needed to commit open/close

export function BottomNav({ items, activeApp, onSelect }: BottomNavProps) {
  const [open, setOpen]       = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragY, setDragY]     = useState(0); // positive = dragging down (closing)

  const startYRef    = useRef(0);
  const startOpenRef = useRef(false);

  // ── Gesture handlers ────────────────────────────────────────────────────────

  const hasDraggedRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    startYRef.current    = e.clientY;
    startOpenRef.current = open;
    hasDraggedRef.current = false;
    setDragging(true);
    setDragY(0);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    const delta = e.clientY - startYRef.current;
    if (Math.abs(delta) > 4) hasDraggedRef.current = true;
    if (!startOpenRef.current) {
      setDragY(Math.min(0, delta));
    } else {
      setDragY(Math.max(0, delta));
    }
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);

    if (!startOpenRef.current && dragY < -SNAP_THRESHOLD) {
      setOpen(true);
    } else if (startOpenRef.current && dragY > SNAP_THRESHOLD) {
      setOpen(false);
    }
    setDragY(0);
  };

  const onPillClick = () => {
    if (hasDraggedRef.current) return;
    setOpen((v) => !v);
  };

  // Close on outside tap
  useEffect(() => {
    if (!open) return;
    const handler = (e: TouchEvent | MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-bottom-nav]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // ── Derived transform ───────────────────────────────────────────────────────

  // Drawer slides up from bottom.
  // drawerFullHeight = nav items + padding + pill+hint area
  const PILL_VISIBLE = 100;
  const drawerFullHeight = items.length * 64 + 48 + 24;

  // When closed: translate down enough that only the pill+hint peeks out
  // When open: no translation (fully visible)
  const baseTranslate = open ? 0 : drawerFullHeight - PILL_VISIBLE;
  const translate = Math.max(0, baseTranslate + dragY);

  const transition = dragging ? 'none' : 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)';

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 md:hidden transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onMouseDown={() => setOpen(false)}
      />

      {/* Drawer */}
      <div
        data-bottom-nav
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background"
        style={{
          transform: `translateY(${translate}px)`,
          transition,
        }}
      >
        {/* Pill handle */}
        <div
          className="flex flex-col items-center justify-center gap-1 pt-2 pb-1 cursor-grab active:cursor-grabbing touch-none select-none bg-background border-t border-border"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onClick={onPillClick}
        >
          {/* Pill */}
          <div className={cn(
            'h-1 rounded-full transition-all duration-200',
            open ? 'w-8 bg-muted-foreground/40' : 'w-10 bg-muted-foreground/60'
          )} />

          {/* Active app hint — only when closed */}
          {!open && (() => {
            const active = items.find(i => i.id === activeApp);
            if (!active) return null;
            const Icon = active.icon;
            return (
              <div className="flex items-center gap-1.5 text-muted-foreground/60 pb-1">
                <Icon size={13} strokeWidth={1.5} />
                <span className="text-[10px]">{active.label}</span>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M1 6 L4 2 L7 6" />
                </svg>
              </div>
            );
          })()}
        </div>

        {/* App list */}
        <nav className="bg-background border-t border-border px-4 pb-6 pt-2">
          {items.map((item) => {
            const isActive = item.id === activeApp;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { onSelect(item.id); setOpen(false); }}
                className={cn(
                  'flex items-center gap-4 w-full px-3 py-4 rounded-xl transition-colors text-left',
                  isActive
                    ? 'text-foreground bg-muted/60'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                )}
              >
                <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                <span className={cn('text-sm', isActive ? 'font-medium' : 'font-normal')}>
                  {item.label}
                </span>
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}